/**
 * Utility to compress images client-side using a canvas. Images are scaled
 * down so their longest edge does not exceed the given maximum and are
 * re-encoded as JPEG. The backend stores the images as they arrive, so all
 * compression has to happen here.
 */
const JPEG_MIME: string = 'image/jpeg';

function loadImage(file: Blob): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(url);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Image could not be loaded'));
        };
        image.src = url;
    });
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Canvas could not be exported'));
            }
        }, JPEG_MIME, quality);
    });
}

/**
 * Compresses the given image so that its longest edge does not exceed
 * maxEdge pixels. The result is always a JPEG blob.
 * @param file the original image file
 * @param maxEdge maximum length of the longest image edge in pixels
 * @param quality the JPEG quality between 0 and 1 (default 0.8)
 */
export async function compressImage(file: Blob, maxEdge: number, quality: number = 0.8): Promise<Blob> {
    const image = await loadImage(file);
    const scale = Math.min(1, maxEdge / Math.max(image.width, image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    const context = canvas.getContext('2d');
    if (!context) {
        // canvas is not supported, fall back to the original file
        return file;
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvasToBlob(canvas, quality);
}
